/**
 * Tiny dev-only logger. No-ops on console in production builds so we never
 * leak diagnostic data to end users' consoles. In production, errors are
 * forwarded to Sentry via `Sentry.captureException` while warnings are
 * forwarded as messages.
 *
 * Usage:
 *   import logger from "../lib/logger";
 *   logger.error("Foo failed:", err);
 *   logger.warn("Position ping failed:", e);
 */
import { Sentry, isProduction } from "./sentry";

function pickError(args) {
  // Find the first real Error instance in the args list; fall back to a
  // synthesised Error built from whatever was passed in.
  for (const a of args) {
    if (a instanceof Error) return a;
  }
  return new Error(args.map((a) => (a == null ? "" : String(a))).join(" "));
}

const logger = {
  error: (...args) => {
    if (!isProduction) {
      console.error(...args);
      return;
    }
    try {
      Sentry.captureException(pickError(args), {
        extra: { args: args.map((a) => (a instanceof Error ? a.message : a)) },
      });
    } catch (sentryErr) {
      // Intentionally swallowed: a Sentry beacon failure (e.g. transport
      // blocked, DSN misconfigured) must never crash the host app. We
      // cannot recurse into logger.error here, and console.* is exactly
      // what this wrapper exists to silence in production.
      void sentryErr;
    }
  },
  warn: (...args) => {
    if (!isProduction) {
      console.warn(...args);
      return;
    }
    try {
      Sentry.captureMessage(args.map((a) => (a == null ? "" : String(a))).join(" "), "warning");
    } catch (sentryErr) {
      // See note in logger.error — Sentry failures are deliberately silent.
      void sentryErr;
    }
  },
};

export default logger;
